import { useMemo, useState } from "react";
import { Plus, Trash2, Clock, CalendarDays, CalendarCheck2, Lock } from "lucide-react";
import Addslotmodal from "./Addslotmodal.jsx";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Starting schedule for a CAD mentor — edit freely or wire this up to a backend later.
const DEFAULT_AVAILABILITY = {
    Monday: [
        { id: "mon-1", start: "10:00", end: "11:00" },
        { id: "mon-2", start: "14:00", end: "15:00" },
    ],
    Tuesday: [{ id: "tue-1", start: "09:00", end: "10:00" }],
    Wednesday: [
        { id: "wed-1", start: "11:00", end: "12:00" },
        { id: "wed-2", start: "16:00", end: "17:00" },
    ],
    Thursday: [{ id: "thu-1", start: "10:00", end: "11:00" }],
    Friday: [{ id: "fri-1", start: "13:00", end: "14:00" }],
    Saturday: [],
    Sunday: [],
};

function getWeekdayName(dateStr) {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return DAYS[(d.getDay() + 6) % 7]; // JS getDay(): Sun=0..Sat=6 -> convert to Mon-first
}

function to12h(t) {
    const [h, m] = t.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

export default function Avalibility({ bookings = [] }) {
    const [availability, setAvailability] = useState(DEFAULT_AVAILABILITY);
    const [closedDays, setClosedDays] = useState(new Set(["Sunday"]));
    const [slotModalDay, setSlotModalDay] = useState(null);

    // Match incoming booking requests to a day + time label so we can mark
    // that slot as booked instead of open for new requests.
    const bookedMap = useMemo(() => {
        const map = new Map();
        bookings.forEach((b) => {
            const day = getWeekdayName(b.date);
            if (!day || !b.time) return;
            map.set(`${day}-${b.time}`, b);
        });
        return map;
    }, [bookings]);

    const stats = useMemo(() => {
        let total = 0;
        let activeDays = 0;
        DAYS.forEach((day) => {
            const slots = availability[day] || [];
            total += slots.length;
            if (slots.length > 0 && !closedDays.has(day)) activeDays += 1;
        });
        const booked = bookedMap.size;
        return { total, available: Math.max(total - booked, 0), booked, activeDays };
    }, [availability, closedDays, bookedMap]);

    const addSlot = (day, start, end) => {
        setAvailability((prev) => ({
            ...prev,
            [day]: [...(prev[day] || []), { id: `${day}-${Date.now()}`, start, end }].sort((a, b) =>
                a.start.localeCompare(b.start)
            ),
        }));
    };

    const removeSlot = (day, id) => {
        setAvailability((prev) => ({
            ...prev,
            [day]: prev[day].filter((s) => s.id !== id),
        }));
    };

    const toggleDayClosed = (day) => {
        setClosedDays((prev) => {
            const next = new Set(prev);
            if (next.has(day)) next.delete(day);
            else next.add(day);
            return next;
        });
    };

    const statCards = [
        { title: "Total Slots", value: stats.total, color: "bg-indigo-100 text-indigo-600", icon: CalendarDays },
        { title: "Available", value: stats.available, color: "bg-green-100 text-green-600", icon: CalendarCheck2 },
        { title: "Booked", value: stats.booked, color: "bg-amber-100 text-amber-600", icon: Lock },
        { title: "Active Days", value: stats.activeDays, color: "bg-cyan-100 text-cyan-600", icon: CalendarDays },
    ];

    return (
        <div className="space-y-6">
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {statCards.map((item, i) => {
                    const Icon = item.icon;
                    return (
                        <div
                            key={i}
                            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">{item.title}</p>
                                    <h2 className="text-3xl font-bold mt-2 text-slate-900">{item.value}</h2>
                                </div>
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${item.color}`}>
                                    <Icon size={22} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Weekly schedule grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {DAYS.map((day) => {
                    const slots = availability[day] || [];
                    const closed = closedDays.has(day);
                    return (
                        <div
                            key={day}
                            className={`rounded-2xl border bg-white p-4 shadow-sm transition ${closed ? "border-slate-100 opacity-60" : "border-slate-200"
                                }`}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <p className="font-semibold text-slate-900">{day}</p>
                                <button
                                    onClick={() => toggleDayClosed(day)}
                                    className={`text-xs font-medium rounded-full px-2.5 py-1 transition-colors ${closed ? "bg-slate-100 text-slate-500" : "bg-green-100 text-green-600"
                                        }`}
                                >
                                    {closed ? "Unavailable" : "Available"}
                                </button>
                            </div>

                            {!closed && (
                                <div className="space-y-2">
                                    {slots.length === 0 && (
                                        <p className="text-xs text-slate-400">No slots added yet.</p>
                                    )}

                                    {slots.map((s) => {
                                        const label = `${to12h(s.start)} - ${to12h(s.end)}`;
                                        const booking = bookedMap.get(`${day}-${label}`) || bookedMap.get(`${day}-${s.start}`);
                                        return (
                                            <div
                                                key={s.id}
                                                className={`flex items-center justify-between rounded-lg border px-3 py-2 text-xs ${booking
                                                    ? "border-amber-200 bg-amber-50 text-amber-700"
                                                    : "border-slate-200 bg-slate-50 text-slate-600"
                                                    }`}
                                            >
                                                <span className="flex items-center gap-1.5">
                                                    <Clock className="h-3.5 w-3.5" />
                                                    {to12h(s.start)} – {to12h(s.end)}
                                                </span>
                                                {booking ? (
                                                    <span className="flex items-center gap-1 font-medium" title={`Booked by ${booking.mentorName}`}>
                                                        <Lock className="h-3 w-3" />
                                                        Booked
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => removeSlot(day, s.id)}
                                                        className="text-slate-400 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}

                                    <button
                                        onClick={() => setSlotModalDay(day)}
                                        className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-indigo-200 px-3 py-2 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    >
                                        <Plus className="h-3.5 w-3.5" />
                                        Add slot
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <Addslotmodal day={slotModalDay} onClose={() => setSlotModalDay(null)} onAdd={addSlot} />
        </div>
    );
}